import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import type { Notification } from "@/types/notification";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL = 60000; // 60 seconds

/**
 * Notifications API response
 */
interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
  has_more: boolean;
}

/**
 * Unread count API response
 */
interface UnreadCountResponse {
  unread_count: number;
}

/**
 * useNotifications hook return type
 */
export interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  hasMore: boolean;
  markAsRead: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  clearAll: () => Promise<boolean>;
  refresh: () => Promise<void>;
}

export const useNotifications = (
  isAuthenticated: boolean = false
): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const fetchNotifications = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get<NotificationsResponse>(
        `${API}/v1/notifications?limit=20`,
        {
          withCredentials: true,
        }
      );
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) return;

    try {
      const response = await axios.get<UnreadCountResponse>(
        `${API}/v1/notifications/unread-count`,
        {
          withCredentials: true,
        }
      );
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(
    async (notificationId: string): Promise<boolean> => {
      try {
        await axios.post(
          `${API}/v1/notifications/${notificationId}/read`,
          {},
          {
            withCredentials: true,
          }
        );

        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));

        return true;
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
        return false;
      }
    },
    []
  );

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    try {
      await axios.post(
        `${API}/v1/notifications/read-all`,
        {},
        {
          withCredentials: true,
        }
      );

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);

      return true;
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      return false;
    }
  }, []);

  const clearAll = useCallback(async (): Promise<boolean> => {
    try {
      await axios.delete(`${API}/v1/notifications/clear-all`, {
        withCredentials: true,
      });

      // Update local state
      setNotifications([]);
      setUnreadCount(0);

      return true;
    } catch (error) {
      console.error("Failed to clear all notifications:", error);
      return false;
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Polling for unread count (lightweight) - only when page is visible
  useEffect(() => {
    if (!isAuthenticated) return;

    let interval: NodeJS.Timeout | null = null;

    const startPolling = (): void => {
      if (!interval) {
        interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
      }
    };

    const stopPolling = (): void => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        // Fetch immediately when becoming visible, then start polling
        fetchUnreadCount();
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Start polling if page is currently visible
    if (document.visibilityState === "visible") {
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    markAsRead,
    markAllAsRead,
    clearAll,
    refresh: fetchNotifications,
  };
};

export default useNotifications;
